import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDetails, StandardDetails } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import { OptionType } from '../../../../../../shared/components/form-builder/form-config.model';
import { useScStore } from '../../../../../../store/scStandard.store';
import { useParams } from 'react-router-dom';
import { notify } from '../../../../../../shared/utils/notify.utills';
import { dtrackItServiceV4 } from '../../../sc-service/dtrackit.service';

interface ScopeCertificateDetailsFormProps {
  formRef: any;
  detailsData: ScopeCertificateDetails;
  setDetailsData: React.Dispatch<React.SetStateAction<ScopeCertificateDetails>>;
  // standardData: StandardDetails[];
  // setStandardsData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  SC_ID?: string;
  isAmendment?: boolean;
  scValidFromDate?: string | number;
}

const ScopeCertificateDetailsForm: React.FC<ScopeCertificateDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
  SC_ID,
  isAmendment,
  scValidFromDate
}) => {
  const { editSCInfo } = useScStore();
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  // Check if scValidFromDate is in the future
  const isFutureDate = scValidFromDate && new Date(String(scValidFromDate)) > new Date();

  // State for dropdown options
  const [statusOptions, setStatusOptions] = useState<OptionType[]>([]);
  const [suspensionReasonOptions, setSuspensionReasonOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);

  // Commented out hardcoded options - now using API calls
  // const allStatusOptions: OptionType[] = [
  //   { label: "Issued (I)", value: "I" }, // Represents "Issued"
  //   { label: "Valid (V)", value: "V" }, // Represents "Valid"
  //   { label: "Expired (E)", value: "E" }, // Represents "Expired"
  //   { label: "Suspended (S)", value: "S" }, // Represents "Suspended"
  //   { label: "Withdrawn (W)", value: "W" }, // Represents "Withdrawn"
  // ];

  // const conformityStatuses: OptionType[] = [
  //   { label: "Critical non-conformity (C)", value: "C" }, // Represents "Critical non-conformity"
  //   { label: "Non-conformity (N)", value: "N" }, // Represents "Non-conformity"
  //   { label: "Administrative (A)", value: "A" }, // Represents "Administrative"
  //   { label: "Other (O)", value: "O" }, // Represents "Other"
  // ];

  // Filter status options based on scValidFromDate
  const allowedFutureStatuses = ['I', 'V'];
  const filteredStatusOptions: OptionType[] = isFutureDate 
    ? statusOptions.filter(option => allowedFutureStatuses.includes(String(option.value))) // Only Issued and Valid for future dates
    : statusOptions; // All options for past/present dates

  // Fetch dropdown options from API
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        setLoading(true);
        
        // Fetch SC Status options
        const scStatusResponse = await dtrackItServiceV4.getSCStatus();
        if (scStatusResponse?.data) {
          const transformedStatusOptions = scStatusResponse.data.map((item: any) => ({
            label: `${item.description_1} (${item.code_1})`,
            value: item.code_1
          }));
          setStatusOptions(transformedStatusOptions);
        }

        // Fetch Suspension Reason options
        const suspensionReasonResponse = await dtrackItServiceV4.getSuspensionReason();
        if (suspensionReasonResponse?.data) {
          const transformedSuspensionOptions = suspensionReasonResponse.data.map((item: any) => ({
            label: `${item.description_1} (${item.code_1})`,
            value: item.code_1
          }));
          setSuspensionReasonOptions(transformedSuspensionOptions);
        }
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
        notify('Failed to load dropdown options', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  // Validation function for SC Number and Version Number
  const validateSCNumberOrVersion = (value: string | undefined) => {
    if (!value) return true; // Let required validation handle empty values
    
    // Check maximum length
    if (value.length > 20) {
      return 'Maximum length is 20 characters';
    }
    
    // Check for restricted keywords (case-insensitive)
    const restrictedKeywords = ['n/a', 'not applicable', 'null', 'na'];
    const lowerValue = value.toLowerCase();
    if (restrictedKeywords.includes(lowerValue)) {
      return 'Restricted keywords (n/a, not applicable, null, na) are not allowed';
    }
    
    // Check for forbidden special characters
    // Allowed: A-Z, a-z, 0-9, hyphen (-), em dash (–)
    const allowedPattern = /^[A-Za-z0-9\-–]+$/;
    if (!allowedPattern.test(value)) {
      return 'Only letters (A-Z, a-z), numbers (0-9), hyphen (-), and em dash (–) are allowed';
    }
    
    return true;
  };
  const formConfig = {
    pageName:"scope-certificate-details",
    initialValues: detailsData,
    validationSchema: Yup.object().shape({
      scopeCertificateNo: Yup.string()
        .required('Scope Certificate No. is required')
        .test('sc-number-validation', function(value) {
          const result = validateSCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        }),
      brandName: Yup.array()
        .of(Yup.string()),
      gotsSCNo: Yup.string(),
      scLegacyNo: Yup.array()
      .of(Yup.string()),
      scCBAuditorId: Yup.array()
      .of(Yup.string()),
      scStandardProgram: Yup.string(),
      scStatus: Yup.string().required("SC Status is required"),
      scStatusDescription: Yup.string(),
      // scWithdrawalReason: Yup.string().when('scStatus', {
      //   is: 'W',
      //   then: (schema) => schema.required('Withdrawal Reason is required'),
      //   otherwise: (schema) => schema.optional()
      // }),
      scSuspensionReason: Yup.string().when('scStatus', {
        is: 'S',
        then: (schema) => schema.required('Suspension Reason is required'),
        otherwise: (schema) => schema.optional()
      }),
      scSuspensionReasonDescription: Yup.string(),
       scVersionNo: Yup.string()
        .required('SC Version No. is required')
        .test('sc-version-validation', function(value) {
          const result = validateSCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        }),
      scProductLimitations: Yup.string(),
      ccsFlag: Yup.string()
        .required('Choose whether CCS Applies'),
      // globalComment: Yup.string(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Scope Certificate Details',
        fields: [
          {
            label: 'Scope Certificate No.',
            name: 'scopeCertificateNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled: (editSCInfo?.scId === SC_ID && editSCInfo?.isParentStatus) ? true : false,
            props: {placeholder: 'Enter Scope Certificate No.' },
            events: {
              onChange: (updatedValue: any, formValues: any, setFieldValue: (arg0: string, arg1: any) => void) => {
                setFieldValue("scVersionNo", updatedValue);
              },
            },
          },
          {
            label: 'SC Version No.',
            name: 'scVersionNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            disabled:  false,
            props: {placeholder: 'Enter SC Version No.' },
          },
          
          {
            label: 'GOTS SC No.',
            name: 'gotsSCNo',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required:false,
            props: { placeholder: 'Enter GOTS SC No. (optional)' },
          },
          {
            label: 'Brand Name',
            name: 'brandName',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter Brand Name(s)',
            },
          },
          
          {
            label: 'SC CB Auditor ID',
            name: 'scCBAuditorId',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter CB Auditor ID',
            },
          },
          {
            label: 'SC Legacy No.',
            name: 'scLegacyNo',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: { placeholder: 'Enter SC Legacy No. (optional)' },
          },
          // {
          //   label: 'SC Standard Program',
          //   name: 'scStandardProgram',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: {
          //     options: [
          //       { label: "None", value: "" },
          //       { label: "Responsible Animal Fiber", value: "RAS,RWS,RAS" },
          //       { label: "Content Claim Standard", value: "CCS" },
          //     ],
          //     placeholder: 'Select Standard Program',
          //   },
          // },
          {
            label: 'SC Status',
            name: 'scStatus',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: filteredStatusOptions,
              placeholder: 'Select SC Status',
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: (field: string, value: any) => void) => {
                // Reset suspension reason if status is not 'S'
                if (value !== 'S') {
                  setFieldValue('scSuspensionReason', '');
                  setFieldValue('scSuspensionReasonDescription', '');
                }
                // Store the description for the selected status
                const selectedOption = filteredStatusOptions.find(option => option.value === value);
                if (selectedOption) {
                  setFieldValue('scStatusDescription', selectedOption.label);
                }
              },
            },
          },
          // {
          //   label: 'SC Withdrawal Reason',
          //   name: 'scWithdrawalReason',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: (values: any) => values.scStatus === "W",
          //   disabled: (values: any) => values.scStatus !== "W",
          //   visible: (values: any) => values.scStatus === "W",
          //   props: {
          //     options: certificationStatuses,
          //     placeholder: 'Select Withdrawal Reason (if applicable)',
          //   },
          // },
          {
            label: 'SC Suspension Reason',
            name: 'scSuspensionReason',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: (values: any) => values.scStatus === "S",
            disabled: (values: any) => values.scStatus !== "S",
            visible: (values: any) => values.scStatus === "S",
            props: {
              options: suspensionReasonOptions,
              placeholder: 'Select Suspension Reason (if applicable)',
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: (field: string, value: any) => void) => {
                // Store the description for the selected suspension reason
                const selectedOption = suspensionReasonOptions.find(option => option.value === value);
                if (selectedOption) {
                  setFieldValue('scSuspensionReasonDescription', selectedOption.label);
                }
              },
            },
          },
          {
            label: 'SC Product Limitations',
            name: 'scProductLimitations',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: false,
            props: {
              placeholder: 'Enter Product Limitations (optional)',
            },
          },
          {
            label: 'Does CCS Apply',
            name: 'ccsFlag',
            type: 'radio',
            gridSize: { xs: 12, md: 6, lg: 4 },
            required: true,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
            },
          },         
          // {
          //   label: 'Global Comment Not For Actual Data To Be Provided',
          //   name: 'globalComment',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 6, lg: 4 },
          //   required: false,
          //   props: { placeholder: 'Enter Global Comment' },
          // },
          // Removed: certifiedOrganizationApprovedClaims field - moved to Standard Details tab
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setDetailsData(values);
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default ScopeCertificateDetailsForm;

